<script setup lang="ts">
import { useSlots, ref, onMounted, onBeforeUnmount, computed } from 'vue'
import lottie, {
	type AnimationItem,
} from 'lottie-web/build/player/lottie_light'
import { cn } from '@/lib/utils'
import animationData from '@/assets/lottie-arrow.json'
import buttonVariants from '.'

type Props = {
	variant?: NonNullable<Parameters<typeof buttonVariants>[0]>['variant']
	size?: NonNullable<Parameters<typeof buttonVariants>[0]>['size']
	as?: string
	isDisabled?: boolean
	isLoading?: boolean
	isFull?: boolean
}

const props = withDefaults(defineProps<Props>(), {
	variant: 'primary',
	size: 'small',
	as: 'button',
	isDisabled: false,
	isLoading: false,
	isFull: false,
})

const slots = useSlots()
const isTertiary = computed(
	() => props.variant === 'tertiary' || props.variant === 'tertiary-inverse',
)
const isButtonDisabled = computed(() => props.isDisabled)

const lottieEl = ref<HTMLDivElement | null>(null)
let lottieAnimation: AnimationItem
const LOTTIE_SPEED = 3

const animateForwards = () => {
	lottieAnimation.setDirection(1)
	lottieAnimation.play()
	lottieAnimation.setSpeed(3)
}

const animateBackwards = () => {
	lottieAnimation.setDirection(-1)
	lottieAnimation.play()
	lottieAnimation.setSpeed(5)
}

const setupLottie = () => {
	if (!lottieEl.value) return
	lottieAnimation = lottie.loadAnimation({
		container: lottieEl.value,
		renderer: 'svg',
		loop: false,
		autoplay: false,
		animationData,
	})

	lottieAnimation.setSpeed(LOTTIE_SPEED)
}

const cleanupLottie = () => {
	lottieAnimation.destroy()
}

onMounted(() => {
	if (isTertiary.value) {
		setupLottie()
	}
})

onBeforeUnmount(() => {
	if (isTertiary.value) {
		cleanupLottie()
	}
})
</script>

<template>
	<component
		:is="as"
		class="baseButton"
		:class="cn(buttonVariants({ variant, size }), $attrs.class ?? '')"
		:disabled="isButtonDisabled"
		v-on="
			isTertiary
				? { mouseenter: animateForwards, mouseleave: animateBackwards }
				: {}
		"
		:data-loading="isLoading"
		:data-full="isFull"
	>
		<template v-if="!isLoading">
			<div
				v-if="slots.leadingIcon"
				class="h-16px w-16px [&>svg]:h-[16px] [&>svg]:w-[16px]"
			>
				<slot name="leadingIcon"></slot>
			</div>

			<span data-btn-text>
				<slot></slot>
			</span>

			<span v-if="slots.textNoUnderline">
				<slot name="textNoUnderline"></slot>
			</span>

			<div
				v-if="slots.trailingIcon"
				class="h-16px w-16px [&>svg]:h-[16px] [&>svg]:w-[16px]"
			>
				<slot name="trailingIcon"></slot>
			</div>
		</template>

		<template v-if="isTertiary">
			<div
				v-show="!isButtonDisabled"
				ref="lottieEl"
				class="[&>svg]:!w-[24px]"
			></div>
			<i
				v-show="isButtonDisabled"
				class="bs-new-icon icon-chevron h-24px w-24px translate-x-[1px]"
			></i>
		</template>

		<div
			v-if="isLoading && (variant === 'primary' || variant === 'pcs-primary')"
			class="flex min-h-[1rem] items-center justify-center gap-8px"
		>
			<div class="h-8px w-8px animate-loading-color-change rounded-full"></div>
			<div
				class="h-8px w-8px animate-loading-color-change rounded-full animation-delay-[0.33s]"
			></div>
			<div
				class="h-8px w-8px animate-loading-color-change rounded-full animation-delay-[0.66s]"
			></div>
		</div>
	</component>
</template>
